import { useState, useEffect } from "react";
import { Phone, Facebook, Clock } from "lucide-react";

const DAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
// Minutes from midnight. Weekdays 8:30am–6pm, weekends 10:30am–3pm.
const WINDOWS = [
  [510, 1080], // Mon
  [510, 1080], // Tue
  [510, 1080], // Wed
  [510, 1080], // Thu
  [510, 1080], // Fri
  [630, 900], // Sat
  [630, 900], // Sun
];

const fmt = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h >= 12 ? "pm" : "am";
  const hh = h % 12 || 12;
  return m ? `${hh}:${String(m).padStart(2, "0")}${period}` : `${hh}${period}`;
};

function aucklandNow() {
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const wd = parts.find((p) => p.type === "weekday").value;
  let hour = parseInt(parts.find((p) => p.type === "hour").value, 10);
  const minute = parseInt(parts.find((p) => p.type === "minute").value, 10);
  if (hour === 24) hour = 0;
  return { idx: DAY_INDEX[wd], minutes: hour * 60 + minute };
}

function getStatus() {
  const { idx, minutes } = aucklandNow();
  const [open, close] = WINDOWS[idx];
  if (minutes >= open && minutes < close) {
    return { open: true, detail: `Open now · closes ${fmt(close)}` };
  }
  if (minutes < open) {
    return { open: false, detail: `Opens ${fmt(open)} today` };
  }
  const ni = (idx + 1) % 7;
  return {
    open: false,
    detail: `Opens ${fmt(WINDOWS[ni][0])} ${
      ni === (idx + 1) % 7 ? "tomorrow" : DAY_NAMES[ni]
    }`,
  };
}

export default function Footer() {
  const [status, setStatus] = useState(getStatus);

  useEffect(() => {
    const tick = () => setStatus(getStatus());
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <footer id="about" data-testid="site-footer" className="bg-forest-ink text-bone">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-20 md:pt-28 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-serif text-[18vw] lg:text-[11vw] leading-[0.82] tracking-tighter">
              Let&rsquo;s <span className="italic text-clay">talk.</span>
            </h2>
            <a
              href="tel:0273943433"
              data-testid="footer-phone"
              className="mt-8 inline-flex items-center gap-3 text-bone/90 hover:text-bone text-lg group"
            >
              <span className="grid place-items-center h-11 w-11 rounded-full border border-bone/25 group-hover:bg-bone group-hover:text-forest-ink transition-colors duration-300">
                <Phone size={18} />
              </span>
              027 394 3433
            </a>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-10 content-start lg:pt-4">
            <div>
              <div className="text-bone/40 text-xs tracking-[0.28em] uppercase mb-4">
                Hours
              </div>
              <div className="flex items-start gap-2.5 text-bone/85">
                <Clock size={16} className="mt-1 shrink-0" />
                <div data-testid="hours-status">
                  <div className="flex items-center gap-2 font-medium">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        status.open ? "bg-emerald-400" : "bg-clay"
                      }`}
                      style={
                        status.open
                          ? { boxShadow: "0 0 0 3px rgba(52,211,153,0.25)" }
                          : undefined
                      }
                    />
                    {status.open ? "Open now" : "Closed now"}
                  </div>
                  <div className="text-bone/55 text-sm mt-1">{status.detail}</div>
                  <div className="mt-4 space-y-1 text-sm text-bone/70">
                    <div className="flex justify-between gap-6">
                      <span>Mon – Fri</span>
                      <span>8:30am – 6pm</span>
                    </div>
                    <div className="flex justify-between gap-6">
                      <span>Sat – Sun</span>
                      <span>10:30am – 3pm</span>
                    </div>
                  </div>
                  <div className="text-bone/35 text-xs mt-3">NZ time · updates live</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-bone/40 text-xs tracking-[0.28em] uppercase mb-4">
                Follow
              </div>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="inline-flex items-center gap-2 text-bone/85 hover:text-bone transition-colors"
              >
                <Facebook size={16} /> Facebook
              </a>
              <div className="mt-6 text-bone/40 text-xs tracking-[0.28em] uppercase mb-4">
                Services
              </div>
              <div className="text-bone/70 text-sm leading-relaxed">
                Painting · Renovations
                <br />
                Fencing · Lawns · Rubbish Removal
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-bone/15 flex flex-col sm:flex-row items-center justify-between gap-4 text-bone/45 text-sm">
          <span className="font-serif text-xl text-bone/80">SideBerms</span>
          <span>
            © {new Date().getFullYear()} SideBerms — Property Maintenance. All rights
            reserved.
          </span>
        </div>
      </div>
    </footer>
  );
}
